import React, { forwardRef } from 'react';
import dayjs from 'dayjs';
import ReportPaper from './report/ReportPaper';
import styles from './EvolutionPhotosReportTemplate.module.css';
import { describeElapsed, formatPhotoDateLong, photoLabel } from '../../pages/appointment/modules/evolutionPhotos';

/**
 * Subtítulo e data da assinatura da comparação (`job` = { before, after, urls }).
 * Compartilhado pelo PDF do módulo e pela seção do relatório completo.
 */
export function evolutionPhotosReportMeta({ before, after }) {
  const elapsed = describeElapsed(before, after);
  const label = before ? photoLabel(before) : '';
  return {
    subtitle: [elapsed, label !== 'Sem identificação' ? label : ''].filter(Boolean).join(' · ') || 'Comparação de fotos',
    date: dayjs().format('DD/MM/YYYY'),
  };
}

/**
 * Miolo do PDF: as duas fotos lado a lado (Antes e Depois) com data,
 * região/ângulo e nota.
 *
 * `before` / `after`: fotos (anexos) comparadas; `after` pode faltar.
 * `urls`: mapa { "<id>:full": "blob:..." } já baixado (loadReportPhotoUrls).
 */
export const EvolutionPhotosReportBody = ({ before, after, urls }) => {
  const sides = [
    { key: 'before', tag: 'Antes', photo: before },
    { key: 'after', tag: 'Depois', photo: after },
  ].filter((side) => side.photo);

  return (
    <div className={styles.compare}>
      {sides.map((side) => {
        const url = (urls || {})[`${side.photo.id}:full`];
        return (
          <div key={side.key} className={styles.side}>
            <div className={styles.sideHead}>
              <span className={styles.tag}>{side.tag}</span>
              <span className={styles.date}>{formatPhotoDateLong(side.photo) || 'Sem data'}</span>
            </div>
            <span className={styles.meta}>{photoLabel(side.photo)}</span>
            <div className={styles.frame}>
              {url
                ? <img className={styles.image} src={url} alt="" />
                : <span className={styles.missing}>Foto indisponível</span>}
            </div>
            {side.photo.note && <span className={styles.note}>{side.photo.note}</span>}
          </div>
        );
      })}
    </div>
  );
};

/** PDF da comparação de fotos de evolução: papel + miolo, com o tempo entre elas no subtítulo. */
const EvolutionPhotosReportTemplate = forwardRef(({ companyData, professional, patientName, ...job }, ref) => {
  const meta = evolutionPhotosReportMeta(job);
  return (
    <ReportPaper
      ref={ref}
      companyData={companyData}
      professional={professional}
      patientName={patientName}
      title="Fotos de evolução"
      subtitle={meta.subtitle}
      date={meta.date}
    >
      <EvolutionPhotosReportBody {...job} />
    </ReportPaper>
  );
});

EvolutionPhotosReportTemplate.displayName = 'EvolutionPhotosReportTemplate';

export default EvolutionPhotosReportTemplate;
